import {
  useState,
  FocusEvent,
  ChangeEvent,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
} from 'react';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ITopic,
  ITopicGroup,
  ITopicTag,
  MessageType,
  TNullable,
  TopicStatus,
} from '../../common/types';
import { QUESTION_QUERY_MIN_LENGTH } from '../../constants/app';
import { Pages } from '../../constants/pages';
import useChat from '../../hooks/use-chat';
import {
  cn,
  findBestTopicSelectionMatchInQuery,
  getExactGroupFromTopicGroupsList,
  getExactTagFromTopicTagsList,
  getExactTopicFromTopicsList,
  getOnlyDisabledTopicFromGroup,
  getOnlyDisabledTopicFromTag,
  normalizeQueryForTopicMatching,
  QUERY_PARAM,
  shouldDisableSearchFieldAutoFocus,
  SUBMIT_QUERY_PARAM,
  shouldUseMaxHeight,
  shouldUseSessionStorage,
  trimQuery,
} from '../../lib/utils';
import { CURRENT_INSTANCE } from '../../tenant';
import { Textarea } from '../ui/textarea';

interface ChatComposerProps {
  query: string;
  setQuery: Dispatch<SetStateAction<string>>;
  topics: ITopic[];
  topicGroups: ITopicGroup[];
  topicTags: ITopicTag[];
  setTopicGroup: Dispatch<SetStateAction<TNullable<ITopicGroup>>>;
  setTopicTag: Dispatch<SetStateAction<TNullable<ITopicTag>>>;
  setQueryIncludesTopicFilter: Dispatch<SetStateAction<boolean>>;
  addStaticMessage: (messages: string, type: MessageType) => void;
  setShowTopicsError: Dispatch<SetStateAction<boolean>>;
  sendQuery: (query: string, topicId: string, disabledTopicName?: string) => Promise<void>;
  ensurePendingQueryLog: (query: string) => Promise<void>;
  disabled: boolean;
}

function ChatComposer(props: ChatComposerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    agreedToDisclaimer,
    agreedToDisclaimerPerSession,
    liveChat,
    setShowAgreementBanner,
    selectedTopic,
    setSelectedTopic,
  } = useChat();
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!shouldDisableSearchFieldAutoFocus() && !props.disabled) {
      textareaRef.current?.focus();
    }
  }, [props.disabled]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [props.query]);

  function handleFocus() {
    setIsFocused(true);
  }

  function handleBlurTextarea(e: FocusEvent<HTMLTextAreaElement>) {
    if (!e.target.value) {
      setIsFocused(false);
    }
  }

  function handleChangeTextarea(e: ChangeEvent<HTMLTextAreaElement>) {
    props.setQuery(e.target.value);
  }

  function handleEnter(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && props.query) {
      e.preventDefault();
      void handleSubmit();
    }
  }

  async function handleSubmit(initialQuery: string = '') {
    const submittedQuery = initialQuery || props.query;
    const queryTrimmed = trimQuery(submittedQuery);
    if (queryTrimmed === '') {
      return;
    }

    if (liveChat.isActive) {
      await liveChat.sendMessage(queryTrimmed);
      props.setQuery('');
      return;
    }

    if (
      (shouldUseSessionStorage() && !agreedToDisclaimerPerSession) ||
      (!shouldUseSessionStorage() && !agreedToDisclaimer)
    ) {
      if (shouldUseMaxHeight()) {
        const params = new URLSearchParams({
          [QUERY_PARAM]: queryTrimmed,
          [SUBMIT_QUERY_PARAM]: 'true',
        });
        navigate(`${Pages.Disclaimer}?${params.toString()}`);
      } else {
        setShowAgreementBanner(true);
      }
      return;
    }
    if (props.topics.length === 0) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      props.addStaticMessage(t('common.general_error'), MessageType.Error);
      props.setQuery('');
      return;
    }
    if (queryTrimmed.length < QUESTION_QUERY_MIN_LENGTH) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      props.addStaticMessage(
        t('chat.response.too_short', { count: QUESTION_QUERY_MIN_LENGTH }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }
    props.setTopicGroup(null);
    props.setTopicTag(null);
    props.setQueryIncludesTopicFilter(false);

    const normalizedQuery = normalizeQueryForTopicMatching(queryTrimmed);

    if (props.topicTags.length > 0) {
      const foundTopicTag = getExactTagFromTopicTagsList(props.topicTags, normalizedQuery);
      if (foundTopicTag) {
        const disabledTopicFromTag = getOnlyDisabledTopicFromTag(props.topics, foundTopicTag);
        if (disabledTopicFromTag) {
          props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
          props.addStaticMessage(
            t('chat.response.disabled_topic', { topic: foundTopicTag.name }),
            MessageType.DisabledTopicMessage
          );
          props.setQuery('');
          props.setShowTopicsError(false);
          return;
        }
        await props.ensurePendingQueryLog(queryTrimmed);
        props.setTopicTag(foundTopicTag);
        props.setShowTopicsError(true);
        return;
      }
    }

    if (props.topicGroups.length > 0) {
      const foundTopicGroup = getExactGroupFromTopicGroupsList(props.topicGroups, normalizedQuery);
      if (foundTopicGroup) {
        const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(props.topics, foundTopicGroup);
        if (disabledTopicFromGroup) {
          props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
          props.addStaticMessage(
            t('chat.response.disabled_topic', { topic: foundTopicGroup.name }),
            MessageType.DisabledTopicMessage
          );
          props.setQuery('');
          props.setShowTopicsError(false);
          return;
        }
        await props.ensurePendingQueryLog(queryTrimmed);
        props.setTopicGroup(foundTopicGroup);
        props.setShowTopicsError(true);
        return;
      }
    }

    const foundTopic = getExactTopicFromTopicsList(props.topics, normalizedQuery);
    if (foundTopic) {
      props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
      if (foundTopic.status === TopicStatus.Disabled) {
        props.addStaticMessage(
          t('chat.response.disabled_topic', { topic: foundTopic.displayName }),
          MessageType.DisabledTopicMessage
        );
        props.setQuery('');
        props.setShowTopicsError(false);
        return;
      }
      setSelectedTopic(foundTopic.id);
      props.addStaticMessage(
        t('chat.response.topic_changed', { topic: foundTopic.displayName }),
        MessageType.Message
      );
      props.setQuery('');
      return;
    }

    const topicSelectionMatch = findBestTopicSelectionMatchInQuery(
      queryTrimmed,
      props.topics,
      props.topicGroups,
      props.topicTags
    );

    if (topicSelectionMatch?.type === 'tag') {
      const matchedTopicTag = topicSelectionMatch.value;
      const disabledTopicFromTag = getOnlyDisabledTopicFromTag(props.topics, matchedTopicTag);
      if (disabledTopicFromTag) {
        props.sendQuery(queryTrimmed, disabledTopicFromTag.id, matchedTopicTag.name);
        props.setShowTopicsError(false);
        return;
      }
      await props.ensurePendingQueryLog(queryTrimmed);
      props.setTopicTag(matchedTopicTag);
      props.setQueryIncludesTopicFilter(true);
      props.setShowTopicsError(true);
      return;
    }

    if (topicSelectionMatch?.type === 'group') {
      const matchedTopicGroup = topicSelectionMatch.value;
      const disabledTopicFromGroup = getOnlyDisabledTopicFromGroup(props.topics, matchedTopicGroup);
      if (disabledTopicFromGroup) {
        props.sendQuery(queryTrimmed, disabledTopicFromGroup.id, matchedTopicGroup.name);
        props.setShowTopicsError(false);
        return;
      }
      await props.ensurePendingQueryLog(queryTrimmed);
      props.setTopicGroup(matchedTopicGroup);
      props.setQueryIncludesTopicFilter(true);
      props.setShowTopicsError(true);
      return;
    }

    if (topicSelectionMatch?.type === 'topic') {
      const matchedTopic = topicSelectionMatch.value;
      if (matchedTopic.status === TopicStatus.Disabled) {
        props.sendQuery(queryTrimmed, matchedTopic.id);
        props.setShowTopicsError(false);
        return;
      }
      setSelectedTopic(matchedTopic.id);
      props.sendQuery(queryTrimmed, matchedTopic.id);
      props.setShowTopicsError(false);
      return;
    }

    if (selectedTopic) {
      const selectedTopicObject = props.topics.find((topic) => topic.id === selectedTopic);
      if (selectedTopicObject?.status === TopicStatus.Disabled) {
        props.addStaticMessage(queryTrimmed, MessageType.UserEntry);
        props.addStaticMessage(
          t('chat.response.disabled_topic', { topic: selectedTopicObject.displayName }),
          MessageType.DisabledTopicMessage
        );
        props.setQuery('');
        props.setShowTopicsError(false);
        return;
      }
      props.sendQuery(queryTrimmed, selectedTopic);
      return;
    } else {
      await props.ensurePendingQueryLog(queryTrimmed);
      props.setShowTopicsError(true);
      return;
    }
  }

  useEffect(() => {
    const initialQuery = searchParams.get(QUERY_PARAM);
    const shouldSubmitQuery = searchParams.get(SUBMIT_QUERY_PARAM) === 'true';
    if (initialQuery && initialQuery.length > 0 && props.topics.length > 0) {
      props.setQuery(initialQuery);
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.delete(QUERY_PARAM);
      updatedSearchParams.delete(SUBMIT_QUERY_PARAM);
      setSearchParams(updatedSearchParams);
      if (shouldSubmitQuery) {
        void handleSubmit(initialQuery);
      }
    }
  }, [props.topics]); // eslint-disable-line react-hooks/exhaustive-deps

  const inputClasses = CURRENT_INSTANCE.theme.classes?.input;

  return (
    <div
      className={cn(
        'relative w-full mt-4 rounded-2xl border border-slate-200/70 bg-white p-3 shadow-[0_10px_30px_rgba(15,23,42,0.06)] animate-pop',
        !shouldUseMaxHeight() && 'mt-5'
      )}
    >
      <label
        htmlFor="question-input"
        className={cn(
          'absolute left-3 transition-all duration-200 z-10',
          isFocused || props.query
            ? `top-1 text-xs text-[hsl(var(--xircle-main))] bg-white/90 px-1 rounded ${props.disabled ? '!text-slate-100' : ''}`
            : 'top-1/2 -translate-y-1/2 text-gray-400',
          inputClasses?.placeholder
        )}
      >
        {liveChat.isActive ? t('chat.live_chat.input_placeholder') : t('chat.input.placeholder')}
      </label>
      <Textarea
        id="question-input"
        disabled={props.disabled}
        ref={textareaRef}
        value={props.query}
        onChange={handleChangeTextarea}
        onFocus={handleFocus}
        onBlur={handleBlurTextarea}
        onKeyDown={handleEnter}
        rows={1}
        className={cn(
          'pt-8 pb-2 px-3 pr-10 bg-transparent text-slate-900 min-h-14 text-base font-normal border-none resize-none overflow-hidden rounded-xl w-full focus:ring-0 focus:outline-none focus-visible:ring-0',
          inputClasses?.textarea
        )}
      />
      <MagnifyingGlass
        size={20}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 cursor-pointer"
        onClick={() => void handleSubmit()}
      />
    </div>
  );
}

export default ChatComposer;
